import type { EmbedConfig, EmbedPosition } from './types'

const SCRIPT_ID = 'vyro-chat-embed'

/**
 * Locate the embedding `<script>` element. Prefers the stable id from the
 * snippet, then `document.currentScript` (valid only during synchronous
 * execution), then any script whose src looks like the chat loader. Returning
 * the element (rather than reading globals) keeps this unit-testable.
 */
export function findScript(
  doc: Document,
  currentScript?: HTMLOrSVGScriptElement | null,
): HTMLScriptElement | null {
  const byId = doc.getElementById(SCRIPT_ID)
  if (byId && byId.tagName === 'SCRIPT') {
    return byId as HTMLScriptElement
  }
  if (currentScript && currentScript.tagName === 'SCRIPT') {
    return currentScript as HTMLScriptElement
  }
  const bySrc = doc.querySelector<HTMLScriptElement>(
    'script[src*="/chat.js"][data-vyro-showroom]',
  )
  return bySrc ?? null
}

function stripTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '')
}

function originFromSrc(src: string | null): string {
  if (!src) return ''
  try {
    return new URL(src, window.location.href).origin
  } catch {
    return ''
  }
}

/**
 * Read {@link EmbedConfig} from the script tag's data attributes.
 *
 * - `data-vyro-showroom` (required) — showroom code.
 * - `data-vyro-host` (optional) — the iframe host origin; defaults to the
 *   origin the loader itself was served from.
 * - `data-vyro-position` (optional) — `bottom-left`; anything else is
 *   `bottom-right`.
 *
 * Returns `null` when the showroom is missing or no host can be resolved — the
 * loader must not guess.
 */
export function resolveConfig(
  scriptEl: HTMLScriptElement | null,
): EmbedConfig | null {
  if (!scriptEl) return null

  const showroom = (scriptEl.getAttribute('data-vyro-showroom') ?? '').trim()
  if (!showroom) return null

  const hostAttr = (scriptEl.getAttribute('data-vyro-host') ?? '').trim()
  const host = stripTrailingSlashes(hostAttr || originFromSrc(scriptEl.src))
  if (!host) return null

  const position: EmbedPosition =
    scriptEl.getAttribute('data-vyro-position') === 'bottom-left'
      ? 'bottom-left'
      : 'bottom-right'

  return { showroom, host, position }
}

/** Origin of the configured host, for postMessage targeting / validation. */
export function hostOrigin(host: string): string {
  try {
    return new URL(host).origin
  } catch {
    return host
  }
}
